import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import appConfig from '../config';
import { 
  FeedbackConfig, 
  FeedbackState, 
  FeedbackData, 
  FeedbackMessage
} from '../types/feedback';
import { FeedbackStorageManager } from '../utils/feedbackStorage';
import { useConversation } from './ConversationContext';

interface FeedbackContextType {
  feedbackConfig: FeedbackConfig | null;
  messageFeedback: Map<string, FeedbackState>;
  loading: boolean;
  error: string | null;
  submitFeedback: (
    messageId: string,
    type: 'positive' | 'negative',
    options?: {
      reasons?: string[];
      comment?: string;
      bypassFlyout?: boolean;
    }
  ) => Promise<void>;
  updateFeedback: (
    messageId: string,
    type: 'positive' | 'negative',
    options?: {
      reasons?: string[];
      comment?: string;
      bypassFlyout?: boolean;
    }
  ) => Promise<void>;
  getFeedbackForMessage: (messageId: string) => FeedbackState | null;
  loadFeedbackFromStorage: (conversationId: string) => void;
  clearFeedbackStorage: () => void;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export const useFeedback = (): FeedbackContextType => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};

interface FeedbackProviderProps {
  children: React.ReactNode;
}

export const FeedbackProvider: React.FC<FeedbackProviderProps> = ({ children }) => {
  const [feedbackConfig, setFeedbackConfig] = useState<FeedbackConfig | null>(null);
  const [messageFeedback, setMessageFeedback] = useState<Map<string, FeedbackState>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { conversationId, sendFeedback: conversationSendFeedback } = useConversation();

  // Load feedback configuration from backend
  const loadFeedbackConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${appConfig.apiUrl}/feedback/config`);
      if (!response.ok) {
        throw new Error('Failed to load feedback configuration');
      }
      const config: FeedbackConfig = await response.json();
      setFeedbackConfig(config);
      setError(null);
    } catch (err) {
      console.error('Failed to load feedback config:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');      // Set minimal config to prevent UI crashes
      setFeedbackConfig({
        enabled: false,
        allowChanges: false,
        negativeFlyoutEnabled: false,
        negativeReasons: {
          multiSelect: false,
          options: []
        },
        commentConfig: {
          enabled: false,
          required: false
        },
        theme: {
          positiveColor: '#4caf50',
          negativeColor: '#f44336',
          neutralColor: '#9e9e9e',
          selectedColor: '#1976d2',
          backgroundColor: '#ffffff',
          textColor: '#000000',
          flyoutBackground: '#ffffff',
          flyoutTitleColor: '#1976d2',
          flyoutBorderColor: '#e0e0e0'
        },        text: {
          positiveTooltip: 'Mark as helpful',
          negativeTooltip: 'Mark as not helpful',
          flyoutTitle: 'Feedback',
          commentPlaceholder: 'Optional comment',
          submitButton: 'Submit',
          cancelButton: 'Cancel',
          confirmationMessage: 'Thank you!'
        }
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load feedback from storage for current conversation
  const loadFeedbackFromStorage = useCallback((conversationId: string) => {
    if (!conversationId) return;

    try {
      const storedFeedback = FeedbackStorageManager.getAllFeedback(conversationId);
      const feedbackMap = new Map<string, FeedbackState>();

      storedFeedback.forEach((data, messageId) => {
        feedbackMap.set(messageId, {
          messageId,
          type: data.type,
          reasons: data.reasons,
          comment: data.comment,
          timestamp: data.timestamp,
          canChange: feedbackConfig?.allowChanges ?? true,
          flyoutBypassed: data.type === 'negative' && !data.reasons?.length
        });
      });

      setMessageFeedback(feedbackMap);
    } catch (error) {
      console.error('Failed to load feedback from storage:', error);
    }
  }, [feedbackConfig?.allowChanges]);  // Send feedback via ConversationContext
  const sendFeedbackMessage = useCallback((message: FeedbackMessage) => {
    conversationSendFeedback(
      message.messageId,
      message.feedbackType,
      message.feedbackReasons,
      message.feedbackComment,
      message.flyoutBypassed
    );
  }, [conversationSendFeedback]);

  // Submit new feedback
  const submitFeedback = useCallback(async (
    messageId: string,
    type: 'positive' | 'negative',
    options?: {
      reasons?: string[];
      comment?: string;
      bypassFlyout?: boolean;
    }
  ) => {
    if (!feedbackConfig?.enabled) {
      console.warn('Feedback system is disabled');
      return;
    }

    try {
      const feedbackMessage: FeedbackMessage = {
        type: 'feedback',
        messageId,
        feedbackType: type,
        feedbackReasons: options?.reasons || [],
        feedbackComment: options?.comment,
        flyoutBypassed: options?.bypassFlyout || false
      };

      // Send to backend (temporarily just log)
      sendFeedbackMessage(feedbackMessage);

      // Update local state
      const feedbackState: FeedbackState = {
        messageId,
        type,
        reasons: options?.reasons,
        comment: options?.comment,
        timestamp: new Date().toISOString(),
        canChange: feedbackConfig.allowChanges,
        flyoutBypassed: options?.bypassFlyout
      };

      setMessageFeedback(prev => new Map(prev.set(messageId, feedbackState)));

      // Persist to storage
      if (conversationId) {
        const feedbackData: FeedbackData = {
          messageId,
          type,
          reasons: options?.reasons,
          comment: options?.comment,
          timestamp: feedbackState.timestamp,
          canChange: feedbackConfig.allowChanges
        };
        FeedbackStorageManager.saveFeedback(conversationId, messageId, feedbackData);
      }

    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error;
    }
  }, [feedbackConfig, conversationId, sendFeedbackMessage]);

  // Update existing feedback
  const updateFeedback = useCallback(async (
    messageId: string,
    type: 'positive' | 'negative',
    options?: {
      reasons?: string[];
      comment?: string;
      bypassFlyout?: boolean;
    }
  ) => {
    if (!feedbackConfig?.allowChanges) {
      console.warn('Feedback changes are not allowed');
      return;
    }

    // Get previous feedback for tracking
    const previousFeedback = messageFeedback.get(messageId);

    // Use submitFeedback with previous feedback tracking
    await submitFeedback(messageId, type, options);

    // Log feedback change for analytics
    if (previousFeedback) {
      console.log('Feedback changed:', {
        messageId,
        from: previousFeedback.type,
        to: type,
        previousReasons: previousFeedback.reasons,
        newReasons: options?.reasons
      });
    }
  }, [feedbackConfig?.allowChanges, messageFeedback, submitFeedback]);

  // Get feedback for a specific message
  const getFeedbackForMessage = useCallback((messageId: string): FeedbackState | null => {
    return messageFeedback.get(messageId) || null;
  }, [messageFeedback]);

  // Clear feedback storage
  const clearFeedbackStorage = useCallback(() => {
    FeedbackStorageManager.clearAllFeedback();
    setMessageFeedback(new Map());
  }, []);  // Load configuration on mount
  useEffect(() => {
    loadFeedbackConfig();
  }, [loadFeedbackConfig]);

  // Load feedback from storage when conversation changes
  useEffect(() => {
    if (conversationId && feedbackConfig) {
      loadFeedbackFromStorage(conversationId);
    }
  }, [conversationId, feedbackConfig, loadFeedbackFromStorage]);

  const value: FeedbackContextType = {
    feedbackConfig,
    messageFeedback,
    loading,
    error,
    submitFeedback,
    updateFeedback,
    getFeedbackForMessage,
    loadFeedbackFromStorage,
    clearFeedbackStorage
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};

export default FeedbackContext;
